/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState } from 'react'
import { X, BookOpen, Folder, Key, Zap, FileText, Download, Sparkles, TrendingUp, DollarSign } from 'lucide-react'

interface UserGuideModalProps {
  onClose: () => void
}

export function UserGuideModal({ onClose }: UserGuideModalProps) {
  const [activeSection, setActiveSection] = useState<'intro' | 'setup' | 'api-keys' | 'llm-selection' | 'moth' | 'workflow'>('intro')

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-background-secondary border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-background-secondary border-b border-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-accent" />
            <h2 className="text-xl font-title">HiveMind User Guide</h2>
          </div>
          <button className="btn-ghost p-1" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Sidebar Navigation */}
          <div className="w-48 border-r border-border overflow-y-auto bg-background-tertiary">
            <nav className="p-2 space-y-1">
              <button
                onClick={() => setActiveSection('intro')}
                className={`w-full text-left px-3 py-2 rounded text-sm font-condensed transition-colors ${
                  activeSection === 'intro' ? 'bg-accent text-background' : 'hover:bg-background-secondary'
                }`}
              >
                What is HiveMind?
              </button>
              <button
                onClick={() => setActiveSection('setup')}
                className={`w-full text-left px-3 py-2 rounded text-sm font-condensed transition-colors ${
                  activeSection === 'setup' ? 'bg-accent text-background' : 'hover:bg-background-secondary'
                }`}
              >
                Getting Started
              </button>
              <button
                onClick={() => setActiveSection('api-keys')}
                className={`w-full text-left px-3 py-2 rounded text-sm font-condensed transition-colors ${
                  activeSection === 'api-keys' ? 'bg-accent text-background' : 'hover:bg-background-secondary'
                }`}
              >
                API Keys Setup
              </button>
              <button
                onClick={() => setActiveSection('llm-selection')}
                className={`w-full text-left px-3 py-2 rounded text-sm font-condensed transition-colors ${
                  activeSection === 'llm-selection' ? 'bg-accent text-background' : 'hover:bg-background-secondary'
                }`}
              >
                LLM Selection Guide
              </button>
              <button
                onClick={() => setActiveSection('moth')}
                className={`w-full text-left px-3 py-2 rounded text-sm font-condensed transition-colors ${
                  activeSection === 'moth' ? 'bg-accent text-background' : 'hover:bg-background-secondary'
                }`}
              >
                MOTH Format
              </button>
              <button
                onClick={() => setActiveSection('workflow')}
                className={`w-full text-left px-3 py-2 rounded text-sm font-condensed transition-colors ${
                  activeSection === 'workflow' ? 'bg-accent text-background' : 'hover:bg-background-secondary'
                }`}
              >
                Workflow & Features
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeSection === 'intro' && (
              <div className="space-y-4 max-w-3xl">
                <h3 className="text-2xl font-title mb-4">What is HiveMind?</h3>

                <div className="space-y-3 text-sm text-foreground-muted leading-relaxed">
                  <p>
                    <strong className="text-foreground">HiveMind</strong> is a browser-based multi-LLM orchestration system that coordinates multiple AI personas to provide comprehensive feedback on your creative work.
                  </p>

                  <p>
                    Instead of getting feedback from just one AI model, HiveMind orchestrates a panel of diverse AI reviewers, each with specialized expertise, to analyze and critique your work from multiple perspectives.
                  </p>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border mt-4">
                    <h4 className="font-title text-sm mb-2 text-foreground">Key Features:</h4>
                    <ul className="space-y-2 text-xs">
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Multi-LLM Orchestration:</strong> Coordinate multiple AI models (Claude, Gemini, GPT-4, Grok, etc.) in parallel</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Folder className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Drag-and-Drop Files:</strong> Drag files from your desktop directly into chat, including images for vision AI analysis</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>MOTH Format:</strong> Structured, token-efficient format for AI communication (70-90% more compact than markdown)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Download className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Export & Share:</strong> Inflate MOTH to Markdown, export as PDF, compress with MOTHify</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Zap className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Smart Features:</strong> Auto peer review, preserve context, LLM logs panel, clear chat, file preview</span>
                      </li>
                    </ul>
                  </div>

                  <h4 className="font-title text-base mt-6 mb-2 text-foreground">Dual-Mode Workflow:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-xs">
                    <li><strong>Brainstorm Mode:</strong> Explore ideas naturally with the orchestrator in conversational dialogue</li>
                    <li><strong>Switch to Peer Review:</strong> When ready, compile your brainstorm into a project document</li>
                    <li><strong>Multi-LLM Feedback:</strong> Your configured AI peers analyze the document using MOTH format</li>
                    <li><strong>Judge Evaluation:</strong> A judge model scores the work and provides recommendations</li>
                    <li><strong>Iterate:</strong> Request revisions from orchestrator or approve the final document</li>
                  </ol>
                </div>
              </div>
            )}

            {activeSection === 'setup' && (
              <div className="space-y-4 max-w-3xl">
                <h3 className="text-2xl font-title mb-4">Getting Started</h3>

                <div className="space-y-4">
                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <div className="flex items-start gap-3">
                      <Folder className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-title text-sm mb-2">1. Set Up Your Workspace</h4>
                        <p className="text-xs text-foreground-muted mb-3 leading-relaxed">
                          On first launch, HiveMind will ask you to select a workspace directory. This is where all your projects will be stored.
                        </p>
                        <div className="space-y-2 text-xs">
                          <p><strong className="text-foreground-secondary">Recommended:</strong> Create a dedicated folder like <code className="bg-background text-accent px-1 py-0.5 rounded">~/Documents/HiveMindProjects/</code></p>
                          <p><strong className="text-foreground-secondary">Alternative:</strong> Skip workspace setup to use browser-only mode (IndexedDB storage)</p>
                          <p className="text-foreground-muted">💡 You can change your workspace anytime in Settings → Workspace tab</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2">2. Create Your First Project</h4>
                    <div className="space-y-2 text-xs text-foreground-muted leading-relaxed">
                      <p>Click the project dropdown in the top-left corner and select <strong>"New Project"</strong></p>
                      <p>Choose from pre-configured persona templates or customize your own AI reviewers:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li><strong>Technical Reviewer:</strong> Code quality, performance, best practices</li>
                        <li><strong>Content Editor:</strong> Clarity, grammar, readability</li>
                        <li><strong>Innovation Advisor:</strong> Creative ideas, novel approaches</li>
                        <li><strong>Custom Personas:</strong> Define your own specialized reviewers</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2">3. Open Existing Repository (Optional)</h4>
                    <div className="space-y-2 text-xs text-foreground-muted leading-relaxed">
                      <p>You can also open any existing folder or Git repository. HiveMind will add a <code className="bg-background text-accent px-1 py-0.5 rounded">.hivemind/</code> folder to store configuration.</p>
                      <p className="text-foreground-muted">💡 Add <code className="bg-background text-accent px-1 py-0.5 rounded">.hivemind/</code> to your <code className="bg-background text-accent px-1 py-0.5 rounded">.gitignore</code> if you don't want to commit AI feedback</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'api-keys' && (
              <div className="space-y-4 max-w-3xl">
                <h3 className="text-2xl font-title mb-4">API Keys Setup</h3>

                <div className="space-y-4">
                  <p className="text-sm text-foreground-muted leading-relaxed">
                    HiveMind needs API keys to communicate with AI providers. Keys are stored securely in your browser's localStorage and work across all projects.
                  </p>

                  <div className="bg-accent/10 border border-accent/20 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Key className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-title text-sm mb-2 text-accent">Recommended: OpenRouter (Easiest Setup)</h4>
                        <p className="text-xs text-foreground-muted leading-relaxed mb-2">
                          OpenRouter provides access to <strong>all major AI models</strong> through a single API key. This is the easiest way to get started!
                        </p>
                        <div className="space-y-2 text-xs">
                          <p><strong className="text-foreground-secondary">1. Get API Key:</strong> Visit <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">openrouter.ai/keys</a></p>
                          <p><strong className="text-foreground-secondary">2. Add Credits:</strong> Add $5-10 to start (pay-as-you-go, no subscription)</p>
                          <p><strong className="text-foreground-secondary">3. Copy Key:</strong> Copy your API key (starts with <code className="bg-background text-accent px-1 py-0.5 rounded">sk-or-...</code>)</p>
                          <p><strong className="text-foreground-secondary">4. Paste in Settings:</strong> Settings → API Keys → OpenRouter API Key</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-3">Alternative: Direct Provider APIs</h4>
                    <p className="text-xs text-foreground-muted mb-3 leading-relaxed">
                      For better rates or specific features, you can use direct API keys from individual providers. HiveMind will automatically use direct APIs when available, falling back to OpenRouter otherwise.
                    </p>

                    <div className="space-y-3 text-xs">
                      <div className="flex items-start gap-3">
                        <div className="w-24 flex-shrink-0 font-condensed text-foreground-secondary">OpenAI</div>
                        <div className="flex-1">
                          <p className="text-foreground-muted"><a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">platform.openai.com/api-keys</a></p>
                          <p className="text-foreground-muted mt-1">Models: GPT-4o, GPT-4 Turbo, GPT-4</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-24 flex-shrink-0 font-condensed text-foreground-secondary">Anthropic</div>
                        <div className="flex-1">
                          <p className="text-foreground-muted"><a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">console.anthropic.com/settings/keys</a></p>
                          <p className="text-foreground-muted mt-1">Models: Claude Sonnet 4.5, Claude Opus</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-24 flex-shrink-0 font-condensed text-foreground-secondary">Google AI</div>
                        <div className="flex-1">
                          <p className="text-foreground-muted"><a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">makersuite.google.com/app/apikey</a></p>
                          <p className="text-foreground-muted mt-1">Models: Gemini 2.5 Pro, Gemini Pro 1.5</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-24 flex-shrink-0 font-condensed text-foreground-secondary">xAI (Grok)</div>
                        <div className="flex-1">
                          <p className="text-foreground-muted"><a href="https://console.x.ai/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">console.x.ai</a></p>
                          <p className="text-foreground-muted mt-1">Models: Grok 2, Grok Beta</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/20 p-3 rounded text-xs text-blue-400">
                    <strong>💡 Smart Routing:</strong> HiveMind automatically uses direct APIs when you have provider-specific keys, falling back to OpenRouter otherwise. This gives you the best of both worlds!
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'llm-selection' && (
              <div className="space-y-4 max-w-3xl">
                <h3 className="text-2xl font-title mb-4">LLM Selection Guide</h3>

                <div className="space-y-4">
                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2">Model Selection in Settings</h4>
                    <p className="text-xs text-foreground-muted leading-relaxed">
                      The Settings modal provides comprehensive tools to help you choose the best models and APIs for your needs.
                      All model dropdowns are organized by <strong className="text-foreground">cost/quality tiers</strong> with detailed pricing and context window information.
                    </p>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-accent" />
                      Model Tiers (6 Categories)
                    </h4>
                    <div className="space-y-3 text-xs text-foreground-muted mt-3">
                      <div>
                        <strong className="text-foreground">💎 Premium - Best in Class ($3-15/M tokens)</strong>
                        <p className="mt-1">Claude Sonnet 4.5, GPT-5, Claude Opus - Highest quality for critical work</p>
                      </div>
                      <div>
                        <strong className="text-foreground">⭐ Premium Affordable ($1-5/M tokens)</strong>
                        <p className="mt-1">Gemini 2.5 Pro (2M context!), Claude 3.5 Sonnet, GPT-4o - Great balance</p>
                      </div>
                      <div>
                        <strong className="text-foreground">💰 Budget - Great Value ($0.15-1/M tokens)</strong>
                        <p className="mt-1">Gemini 2.0 Flash (1M context!), GPT-4o Mini - Best budget option</p>
                      </div>
                      <div>
                        <strong className="text-foreground">💸 Ultra-Cheap - US Based ($0.18-0.20/M tokens)</strong>
                        <p className="mt-1">Groq Llama 3.1 70B (BLAZING FAST), Together Llama 70B - US-hosted, ultra-cheap</p>
                      </div>
                      <div>
                        <strong className="text-foreground">🔥 Ultra-Cheap - Absolute Lowest ($0.01-0.10/M tokens)</strong>
                        <p className="mt-1">DeepSeek V3 ($0.014/M - CHEAPEST!), Qwen 3 (THINKING MODE 🧠) - Up to 225x cheaper!</p>
                      </div>
                      <div>
                        <strong className="text-foreground">🔓 Open-Source - Large Models</strong>
                        <p className="mt-1">Llama 3.1 405B/70B, Qwen 3, Qwen 2.5 - Fully open source</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-accent" />
                      LLM Leaderboards
                    </h4>
                    <p className="text-xs text-foreground-muted leading-relaxed mb-3">
                      Click "View Leaderboard" buttons in Settings to browse real-world rankings from trusted sources:
                    </p>
                    <ul className="space-y-2 text-xs text-foreground-muted">
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>LMArena:</strong> General LLM rankings based on ELO ratings from human preferences</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Aider.chat:</strong> Coding-specific benchmarks (HumanEval, Exercism, Polyglot)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Judge Arena:</strong> Judge model rankings for quality assessment</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>HuggingFace & Artificial Analysis:</strong> Comprehensive benchmarks and performance metrics</span>
                      </li>
                    </ul>
                    <p className="text-xs text-foreground-muted mt-3">
                      Click "Use This" on any available model to instantly select it!
                    </p>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-accent" />
                      Cost Savings Tab
                    </h4>
                    <p className="text-xs text-foreground-muted leading-relaxed mb-3">
                      The Cost Savings tab shows ultra-low cost providers with direct API integrations:
                    </p>
                    <div className="space-y-2 text-xs text-foreground-muted">
                      <div>
                        <strong className="text-foreground">DeepSeek API</strong> - $0.014-0.028/M tokens (225x cheaper than OpenRouter!)
                      </div>
                      <div>
                        <strong className="text-foreground">Groq API</strong> - ~$0.20/M tokens, blazing fast inference
                      </div>
                      <div>
                        <strong className="text-foreground">Together AI</strong> - Free tier + $0.06-0.20/M tokens
                      </div>
                      <div>
                        <strong className="text-foreground">SiliconFlow</strong> - $0.05-0.086/M tokens
                      </div>
                    </div>
                    <p className="text-xs text-accent mt-3">
                      💡 Add direct API keys to bypass OpenRouter markup fees and save 5-225x on costs!
                    </p>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2">Model Tiers Tab</h4>
                    <p className="text-xs text-foreground-muted leading-relaxed mb-3">
                      Quick selection by tier and geographic origin with auto-apply toggles:
                    </p>
                    <ul className="space-y-2 text-xs text-foreground-muted">
                      <li>• <strong>Tier Preference:</strong> Ultra-Cheap, Budget, Premium-Cheap, Premium-Expensive</li>
                      <li>• <strong>Geographic Origin:</strong> US/EU, China, International, All</li>
                      <li>• <strong>Auto-Apply:</strong> Automatically set orchestrator/judge/peers to selected tier</li>
                    </ul>
                  </div>

                  <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-lg">
                    <h4 className="font-title text-sm mb-2 text-green-400">💡 Strategic Model Selection Tips</h4>
                    <ul className="space-y-2 text-xs text-green-400/90">
                      <li>• <strong>Orchestrator:</strong> Use large context models (Gemini 2.5 Pro 2M, Gemini Flash 1M)</li>
                      <li>• <strong>Judge:</strong> Use high-quality models (Claude Sonnet 4.5, GPT-5) for accurate scoring</li>
                      <li>• <strong>Peers - Mix Tiers:</strong> Premium for critical analysis, budget for quick reviews</li>
                      <li>• <strong>Coding Projects:</strong> Check Aider leaderboard for best coding models</li>
                      <li>• <strong>Cost Optimization:</strong> Use DeepSeek V3 ($0.014/M) for bulk processing</li>
                      <li>• <strong>Thinking Mode:</strong> Try Qwen 3 models (🧠) for complex reasoning tasks</li>
                      <li>• <strong>Speed:</strong> Use Groq for blazing fast responses (~$0.20/M)</li>
                      <li>• <strong>Quality:</strong> Claude Sonnet 4.5 consistently ranks #1 across benchmarks</li>
                    </ul>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/20 p-3 rounded text-xs text-blue-400">
                    <strong>🎯 Quick Tier Selection:</strong> Use the tier buttons in Model Selection tab to instantly set orchestrator, judge, and all peers to a specific tier (Ultra-Cheap US, Cheapest Overall, Budget, Premium).
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'moth' && (
              <div className="space-y-4 max-w-3xl">
                <h3 className="text-2xl font-title mb-4">MOTH Format</h3>

                <div className="space-y-4">
                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2">What is MOTH?</h4>
                    <p className="text-xs text-foreground-muted leading-relaxed">
                      <strong className="text-foreground">MOTH</strong> (Minimal Overhead Technical Hierarchy) is a structured, token-efficient format for AI communication. Think of it as a standardized way for AI models to exchange structured information.
                    </p>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2">Why Use MOTH?</h4>
                    <ul className="space-y-2 text-xs text-foreground-muted">
                      <li className="flex items-start gap-2">
                        <Zap className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Token Efficiency:</strong> Uses 40-60% fewer tokens than markdown, saving costs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Structured Data:</strong> Easy to parse and process programmatically</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong>Consistency:</strong> All AI responses follow the same format for easier analysis</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-background p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-2">Example MOTH Format:</h4>
                    <pre className="text-xs bg-background-tertiary p-3 rounded overflow-x-auto">
{`[CRITIQUE]
persona:Technical_Reviewer
role:Code Quality Analyst

[SCORES]
correctness:{score=0.85; reason=Logic is sound}
performance:{score=0.75; reason=Can be optimized}
maintainability:{score=0.90; reason=Clean code}

[RECOMMENDATIONS]
priority:high; category:performance; description=Add caching layer
priority:medium; category:testing; description=Increase test coverage`}
                    </pre>
                  </div>

                  <div className="bg-accent/10 border border-accent/20 p-4 rounded-lg">
                    <h4 className="font-title text-sm mb-2 text-accent">Inflate to Markdown</h4>
                    <p className="text-xs text-foreground-muted leading-relaxed mb-3">
                      MOTH is compact but not human-friendly. Click the <strong>"Inflate"</strong> button to convert MOTH feedback into beautiful, readable Markdown.
                    </p>
                    <div className="space-y-2 text-xs">
                      <p><strong className="text-foreground-secondary">1. Select Response:</strong> Click any peer response or judge result</p>
                      <p><strong className="text-foreground-secondary">2. Click "Inflate":</strong> Opens the inflation panel on the right</p>
                      <p><strong className="text-foreground-secondary">3. Choose Model:</strong> Select which AI to use for conversion</p>
                      <p><strong className="text-foreground-secondary">4. Export PDF:</strong> Download the inflated markdown as a PDF document</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'workflow' && (
              <div className="space-y-4 max-w-3xl">
                <h3 className="text-2xl font-title mb-4">Workflow & Features</h3>

                <div className="space-y-4">
                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-3">Dual-Mode Workflow</h4>

                    <div className="mb-4">
                      <strong className="text-foreground text-xs">Brainstorm Mode</strong>
                      <p className="text-xs text-foreground-muted mt-1 mb-2">Explore ideas through natural conversation</p>
                      <ol className="space-y-2 text-xs text-foreground-muted ml-4">
                        <li className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-accent/70 text-background rounded-full flex items-center justify-center font-condensed text-xs">1</span>
                          <div><strong className="text-foreground">Chat Naturally:</strong> Discuss ideas, ask questions, refine concepts with the orchestrator</div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-accent/70 text-background rounded-full flex items-center justify-center font-condensed text-xs">2</span>
                          <div><strong className="text-foreground">Build Context:</strong> Have a conversational dialogue until you're ready for structured review</div>
                        </li>
                      </ol>
                    </div>

                    <div>
                      <strong className="text-foreground text-xs">Peer Review Mode</strong>
                      <p className="text-xs text-foreground-muted mt-1 mb-2">Get structured multi-LLM feedback</p>
                      <ol className="space-y-2 text-xs text-foreground-muted ml-4">
                        <li className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-accent/70 text-background rounded-full flex items-center justify-center font-condensed text-xs">3</span>
                          <div><strong className="text-foreground">Switch Modes:</strong> Click "Peer Review" toggle to compile your brainstorm into a document</div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-accent/70 text-background rounded-full flex items-center justify-center font-condensed text-xs">4</span>
                          <div><strong className="text-foreground">Review Document:</strong> Edit the compiled document, then approve to send to peers</div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-accent/70 text-background rounded-full flex items-center justify-center font-condensed text-xs">5</span>
                          <div><strong className="text-foreground">Peer Feedback:</strong> Your configured AI reviewers analyze the document in MOTH format</div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-accent/70 text-background rounded-full flex items-center justify-center font-condensed text-xs">6</span>
                          <div><strong className="text-foreground">Judge Score:</strong> Judge evaluates all feedback and provides a quality score</div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-accent/70 text-background rounded-full flex items-center justify-center font-condensed text-xs">7</span>
                          <div><strong className="text-foreground">Request Revisions:</strong> Send judge feedback to orchestrator for improvements, or approve to finish</div>
                        </li>
                      </ol>
                    </div>
                  </div>

                  <div className="bg-background-tertiary p-4 rounded-lg border border-border">
                    <h4 className="font-title text-sm mb-3">Key Features</h4>

                    <div className="space-y-3 text-xs">
                      <div>
                        <strong className="text-foreground">Dual Modes (Brainstorm + Peer Review):</strong>
                        <p className="text-foreground-muted mt-1">Start in Brainstorm mode for natural conversation, then switch to Peer Review mode for structured multi-LLM feedback. Documents are automatically saved when approved.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">Drag-and-Drop Files & Images:</strong>
                        <p className="text-foreground-muted mt-1">Drag files from your desktop directly into chat. Supports text files and images (PNG, JPG, GIF, WEBP) for vision AI analysis. 10MB file size limit.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">PDF Export & MOTHify:</strong>
                        <p className="text-foreground-muted mt-1">Export inflated markdown to beautifully formatted PDFs. Use "Compress with MOTHify" to reduce document size by 70-90% before sending.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">111 Specialized Personas:</strong>
                        <p className="text-foreground-muted mt-1">Choose from code reviewers, content editors, creative advisors, and domain experts. Configure your ideal review panel.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">LLM Logs Panel:</strong>
                        <p className="text-foreground-muted mt-1">Real-time request/response tracking in the left sidebar. Monitor API calls, token usage, and costs. Resizable panel (100-500px).</p>
                      </div>

                      <div>
                        <strong className="text-foreground">Preserve Context & Auto Review:</strong>
                        <p className="text-foreground-muted mt-1">Enable "Preserve Context" for peers to maintain conversation history. Use "Auto Peer Review" for automatic review on send in Peer Review mode.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">File Attachments:</strong>
                        <p className="text-foreground-muted mt-1">Browse and attach files from your project to include context in your requests. Files are compressed to MOTH format to save tokens.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">Markdown Rendering:</strong>
                        <p className="text-foreground-muted mt-1">Chat responses are beautifully formatted with proper markdown rendering for headings, lists, code blocks, and more.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">Request Revisions:</strong>
                        <p className="text-foreground-muted mt-1">Send judge feedback back to the orchestrator to request improvements before approving the final document.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">Auto Review:</strong>
                        <p className="text-foreground-muted mt-1">Enable auto peer review in Peer Review mode to automatically send orchestrator responses to your review panel.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">Model Selection:</strong>
                        <p className="text-foreground-muted mt-1">Choose different models for orchestrator, peers, and judge. Mix and match to find your ideal configuration.</p>
                      </div>

                      <div>
                        <strong className="text-foreground">Export & Share:</strong>
                        <p className="text-foreground-muted mt-1">Inflate MOTH to Markdown, export as PDF, or save the raw MOTH files for later analysis.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-lg">
                    <h4 className="font-title text-sm mb-2 text-green-400">💡 Pro Tips</h4>
                    <ul className="space-y-2 text-xs text-green-400/90">
                      <li>• Start in Brainstorm mode to explore ideas before getting structured feedback</li>
                      <li>• Drag images from your desktop for vision AI analysis (screenshots, diagrams, photos)</li>
                      <li>• Use "Compress with MOTHify" before sending large documents to save tokens</li>
                      <li>• Enable "Preserve Context" for peers to maintain conversation history across orchestrations</li>
                      <li>• Enable "Auto Peer Review" in Peer Review mode for seamless workflow</li>
                      <li>• Use "Clear Chat" button to start fresh conversations without losing project data</li>
                      <li>• Check LLM Logs panel to monitor API calls and debug issues</li>
                      <li>• Request revisions to iterate with the orchestrator based on judge feedback</li>
                      <li>• Experiment with different judge models - some are better at specific domains</li>
                      <li>• Choose specialized personas that match your project's needs</li>
                      <li>• Export to PDF for sharing polished documents outside HiveMind</li>
                      <li>• Use the "Include attached docs" option when relevant context is in files</li>
                      <li>• Compiled brainstorm documents are auto-saved as MOTH files</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
